import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useOnboardingStore } from "../../store/onboardingSlice";
import { saveOnboardingStep, createStaffMember } from "../../api/auth";
import { trackOnboardingStepComplete } from "../../lib/analytics";

interface StaffEntry {
  full_name: string;
  bio: string;
}

export default function StaffStep() {
  const { setCurrentStep, setStepData, setSaving, saving } = useOnboardingStore();
  const [staff, setStaff] = useState<StaffEntry[]>([]);
  const [fullName, setFullName] = useState("");
  const [bio, setBio] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addMember = () => {
    if (!fullName.trim()) return;
    setStaff((s) => [...s, { full_name: fullName.trim(), bio: bio.trim() }]);
    setFullName("");
    setBio("");
  };

  const removeMember = (idx: number) => {
    setStaff((s) => s.filter((_, i) => i !== idx));
  };

  const handleContinue = async () => {
    setError(null);
    setSaving(true);
    try {
      for (const member of staff) {
        await createStaffMember({ full_name: member.full_name, bio: member.bio || undefined });
      }
      setStepData("staff", staff);
      await saveOnboardingStep({ step: "staff", step_status: staff.length > 0 ? "complete" : "skipped" });
      trackOnboardingStepComplete(4, "staff", true);
      setCurrentStep(5);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const serverMsg = axiosErr?.response?.data?.error;
      console.error("createStaffMember error:", axiosErr?.response?.data ?? axiosErr?.message);
      setError(serverMsg || axiosErr?.message || "Failed to save staff. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await saveOnboardingStep({ step: "staff", step_status: "skipped" });
      trackOnboardingStepComplete(4, "staff", true);
      setCurrentStep(5);
    } catch {
      setError("Failed to skip staff. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Add your staff</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Invite team members. They'll receive an email to set up their account.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          sx={{ flex: "1 1 180px" }}
          size="small"
          autoFocus
        />
        <TextField
          label="Bio (optional)"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          sx={{ flex: "2 1 260px" }}
          size="small"
        />
        <Button variant="outlined" onClick={addMember} disabled={!fullName.trim()}>
          Add
        </Button>
      </Box>

      {staff.length > 0 && (
        <List dense sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 1 }}>
          {staff.map((m, i) => (
            <ListItem key={i} divider={i < staff.length - 1}>
              <ListItemText primary={m.full_name} secondary={m.bio || undefined} />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => removeMember(i)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={saving}
          sx={{ flex: 1 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Continue"}
        </Button>
        <Button variant="text" onClick={handleSkip} disabled={saving}>
          Skip for now
        </Button>
      </Box>
    </Box>
  );
}
